/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import React, { useCallback } from 'react';

import { ChannelOverlay } from '@ibm-watson/shared/modules/channels/components/channel-overlay/ChannelOverlay';
import { SearchOverlay } from '@ibm-watson/shared/modules/discovery/components/search-overlay/SearchOverlay';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { PortalTypes } from '../../../../ts/portal';
import { CustomServiceOverlay } from '../../../custom-service/components/custom-service-overlay/CustomServiceOverlay';
import { ElasticSearchOverlay } from '../../../elastic-search/components/elastic-search-overlay/ElasticSearchOverlay';
import { MilvusOverlay } from '../../../milvus/components/MilvusOverlay';
import { popPortal } from '../../store/portals/reducer';
import { SearchChoiceModal } from '../search-choice-modal/SearchChoiceModal';

const PortalConductor = () => {
  const dispatch = useAppDispatch();
  const portal = useAppSelector(state => state.portals.stack[0]);

  const onClose = portal?.props?.onClose;
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
    dispatch(popPortal());
  }, [dispatch, onClose]);

  if (!portal) {
    return null;
  }

  const { type, props } = portal;
  switch (type) {
    case PortalTypes.SEARCH_CHOICE_OVERLAY: {
      return <SearchChoiceModal {...props} onClose={handleClose} />;
    }
    case PortalTypes.DISCOVERY_SEARCH_OVERLAY: {
      return <SearchOverlay {...props} onClose={handleClose} />;
    }
    case PortalTypes.ELASTIC_SEARCH_OVERLAY: {
      return <ElasticSearchOverlay {...props} onClose={handleClose} />;
    }
    case PortalTypes.MILVUS_OVERLAY: {
      return <MilvusOverlay {...props} onClose={handleClose} />;
    }
    case PortalTypes.CHANNEL_OVERLAY:
      return (
        <ChannelOverlay
          channelType={props.type as string}
          {...props}
          onClose={handleClose}
        />
      );
    case PortalTypes.CUSTOM_SERVICE_OVERLAY: {
      return <CustomServiceOverlay {...props} onClose={handleClose} />;
    }
    default:
      return null;
  }
};

export default PortalConductor;
